/**
 * Epic 123 — federation runtime for the kairos-web shell.
 *
 * **Strategy: federation-lite (native ESM dynamic import).** Story 123.1.
 *
 * Instead of bringing in `@module-federation/vite` and its shared-deps
 * complexity for a first cut, the shell loads remote modules via native
 * `await import(url)`. Remotes publish a single ESM module at a known
 * URL that re-exports a default React component (and optionally a
 * `routes` array). The host fetches it dynamically when a capability
 * is effective.
 *
 * Trade-off vs formal Module Federation:
 *
 *   + No new build-time dep, no pnpm install dance, no host/remote
 *     bundler coupling. Works in dev (Vite serves the remote at a
 *     fixed URL) and in prod (the remote is a static ESM bundle at
 *     a CDN / S3 URL).
 *   − Each remote ships its own React. For the placeholder pilot
 *     (story 123.4) and the GEDU remote (story 123.5, deferred) this
 *     adds a few KB; acceptable for v1. If shared-deps savings become
 *     necessary, this seam graduates cleanly to @module-federation/vite
 *     without changing the public hook API ([useRemoteModule]).
 *
 * Cache: every URL is loaded at most once per session via a module-
 * level `Map`. Two capabilities pointing at the same URL share the
 * same instance.
 */

/**
 * The federation contract (123.5). A remote exports a `mount` function
 * that renders itself into a host-provided element using its OWN React
 * root, and returns an unmount function.
 *
 * **Why mount() and not a default React component:** the shell is React
 * 19 and a remote may bundle a different React major (Educatio is React
 * 18.3). A React component rendered inside the shell's tree would cross
 * React copies → "invalid hook call". So each remote owns its render
 * tree (its own `createRoot`, its own bundled React — NOT external, NO
 * shared import map). Host↔remote state flows ONLY through the global
 * `__KAIROS_SESSION__` bridge (sessionBridge.ts), never React context
 * (not shareable across React copies/majors).
 *
 * **Remote API base MUST be absolute, derived from `import.meta.url`.** When
 * embedded, the remote runs on the SHELL's origin (kairos.demo), so any
 * relative `fetch('/api/...')` resolves against the shell and 404s — the remote
 * never reaches its own backend. A federated remote must call its backend with
 * an absolute base derived from where its module was loaded
 * (`new URL('.', import.meta.url).origin`), NOT `window.location`. (Standalone,
 * relative happens to work because the module and backend share an origin;
 * embedded it breaks.)
 */
export interface RemoteModuleExport {
  mount(el: HTMLElement, ctx: RemoteMountContext): RemoteUnmount;
}

/**
 * Which chrome regions the HOST owns (ADR-015 — negotiated delegation). For each
 * region the host claims (`true`), the remote MUST suppress its own rendering of
 * it and rely on the host; for each region NOT claimed (`false`), the remote
 * renders it itself (its standalone fallback). A zero-capability host — or a
 * foreign host that passes none — yields a fully standalone plugin. Kairos is
 * the maximal host; a foreign CRM is a minimal one.
 */
export interface HostCapabilities {
  /** Host renders the global top bar (brand, notifications, credits). */
  ownsTopBar: boolean;
  /**
   * Host renders identity controls (login/logout, role switch, language). When
   * true the remote renders NO auth/role/language UI and sources identity from
   * the `__KAIROS_SESSION__` bridge (subscribing for role/language changes).
   */
  ownsIdentity: boolean;
  /**
   * Host renders the left nav from the remote's manifest. When true the remote
   * renders no sidebar and exports its navigation for the host. v1: false — the
   * remote keeps its own left nav.
   */
  ownsSideNav: boolean;
  /**
   * Host owns the URL/router. When true the remote delegates navigation to the
   * host and reads the active path from it. v1: false — the remote keeps its own
   * internal router under `basePath`.
   */
  ownsRouting: boolean;
}

/** Context the shell passes to a remote at mount time. */
export interface RemoteMountContext {
  /** Capability key the shell mounted this remote under. */
  capabilityKey: string;
  /** Base path the shell nests the remote under (e.g. `/r/<key>`). */
  basePath: string;
  /** Chrome regions the host owns (ADR-015). The remote suppresses what the host owns. */
  host: HostCapabilities;
}

/**
 * The capabilities Kairos claims TODAY (ADR-015 v1 cut): it owns the global top
 * bar and identity (single top bar, single user/role/language control); the
 * plugin keeps its own left nav + content + internal router. `ownsSideNav` /
 * `ownsRouting` are defined but stay false until the v2 nav-manifest work.
 */
export const KAIROS_HOST_CAPABILITIES_V1: HostCapabilities = {
  ownsTopBar: true,
  ownsIdentity: true,
  ownsSideNav: false,
  ownsRouting: false,
};

/** Tears down the remote's React root; called by the shell on unmount. */
export type RemoteUnmount = () => void;

const cache: Map<string, Promise<RemoteModuleExport>> = new Map();

export function loadRemoteModule(url: string): Promise<RemoteModuleExport> {
  const existing = cache.get(url);
  if (existing) return existing;
  // Vite's static-analysis must not try to resolve this URL at build
  // time — it is genuinely dynamic. The `@vite-ignore` directive
  // disables that and lets the runtime fetch the URL as-is.
  const loading = import(/* @vite-ignore */ url) as Promise<RemoteModuleExport>;
  cache.set(url, loading);
  // If the load fails, drop the cache entry so a later retry has a
  // chance — otherwise a transient network error would poison the
  // shell for the session.
  loading.catch(() => cache.delete(url));
  return loading;
}

/** Clears the in-memory cache; used on logout. */
export function clearRemoteModuleCache(): void {
  cache.clear();
}
